import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const MODES = [
  { key: "text", label: "Livre essentiellement texte", desc: "Roman, essai, récit ou ouvrage principalement en texte continu.", base: 149, unit: "+1,25 € / 1 000 caractères supp." },
  { key: "structured", label: "Livre structuré", desc: "Plusieurs niveaux de titres, notes, citations, encadrés, tableaux…", base: 179, unit: "+1,50 € / 1 000 caractères supp." },
  { key: "illustrated", label: "Livre illustré", desc: "Texte + illustrations, photos, dessins. Seules les pages illustrées facturent le supplément.", base: 179, unit: "+1,50 € / 1 000 caractères supp. · +7 € / page illustrée" },
  { key: "magazine", label: "Magazine / revue (maquette existante)", desc: "Vous disposez déjà d'une maquette éditoriale.", base: 0, unit: "18 € / page" },
  { key: "magazine_new", label: "Nouvelle maquette + mise en page magazine", desc: "Création de l'identité éditoriale, puis mise en page.", base: 199, unit: "199 € + 18 € / page" },
  { key: "brochure", label: "Brochure / livret", desc: "Document multipage assemblé (≥ 4 pages recommandé).", base: 0, unit: "14 € / page" },
];

const DESTS = [
  { k: "imprimeur", l: "Imprimeur professionnel" },
  { k: "perso", l: "Impression personnelle" },
  { k: "digital", l: "Utilisation numérique" },
  { k: "both", l: "Impression + numérique" },
  { k: "unknown", l: "Je ne sais pas encore" },
];

function fmtEUR(n) { return `${(Number(n) || 0).toFixed(2)} €`; }

export default function EditionConfigurator() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rawType = (params.get("type") || params.get("mode") || "text").toLowerCase();
  const initialMode = rawType === "new-magazine" ? "magazine_new" : rawType;
  const [mode, setMode] = useState(initialMode);
  const [title, setTitle] = useState("");
  const [characters, setCharacters] = useState("");
  const [pages, setPages] = useState("");
  const [illustratedPages, setIllustratedPages] = useState("");
  const [totalPagesEst, setTotalPagesEst] = useState("");
  const [hasLayout, setHasLayout] = useState(null);
  const [format, setFormat] = useState("");
  const [destination, setDestination] = useState("imprimeur");
  const [printer, setPrinter] = useState("");
  const [printerSpecs, setPrinterSpecs] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [specsFiles, setSpecsFiles] = useState([]);
  const [confirmChk, setConfirmChk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isCharBased = mode === "text" || mode === "structured" || mode === "illustrated";
  const isPageBased = !isCharBased;
  const magazineMissing = mode === "magazine" && hasLayout === false;
  // Bug #7 — pages illustrées ≤ pages totales estimées
  const illustratedInvalid = useMemo(() => {
    if (mode !== "illustrated") return false;
    const ip = Number(illustratedPages) || 0;
    const cap = Number(totalPagesEst) || 0;
    return cap > 0 && ip > cap;
  }, [mode, illustratedPages, totalPagesEst]);

  const quote = useMemo(() => {
    const c = Number(characters) || 0;
    const p = Number(pages) || 0;
    const ip = Number(illustratedPages) || 0;
    let base = 0, extra = 0, illus = 0, pageCost = 0, mBase = 0;
    if (mode === "text") { base = 149; extra = Math.ceil(Math.max(c - 1000, 0) / 1000) * 1.25; }
    else if (mode === "structured") { base = 179; extra = Math.ceil(Math.max(c - 1000, 0) / 1000) * 1.5; }
    else if (mode === "illustrated") { base = 179; extra = Math.ceil(Math.max(c - 1000, 0) / 1000) * 1.5; illus = ip * 7; }
    else if (mode === "magazine") { pageCost = p * 18; }
    else if (mode === "magazine_new") { mBase = 199; pageCost = p * 18; }
    else if (mode === "brochure") { pageCost = p * 14; }
    const total = base + extra + illus + pageCost + mBase;
    const rows = [];
    if (base) rows.push({ label: `Base ${mode === "brochure" ? "création de maquette" : mode.startsWith("magazine") ? "" : "jusqu'à 1 000 caractères"}`.trim(), amt: base });
    if (mBase) rows.push({ label: "Création de maquette éditoriale", amt: mBase });
    if (extra) rows.push({ label: `${Math.ceil(Math.max(c - 1000, 0) / 1000)} tranche(s) × ${mode === "text" ? "1,25" : "1,50"} €`, amt: extra });
    if (illus) rows.push({ label: `${ip} page(s) illustrée(s) × 7 €`, amt: illus });
    if (pageCost) rows.push({ label: `${p} page(s) × ${mode === "brochure" ? "14" : "18"} €`, amt: pageCost });
    return { rows, total };
  }, [mode, characters, pages, illustratedPages]);

  const submit = async () => {
    if (!confirmChk) { toast.error("Cochez la confirmation avant d'envoyer."); return; }
    if (magazineMissing) { toast.error("Utilisez « Nouvelle maquette + mise en page »."); return; }
    if (illustratedInvalid) {
      toast.error("Le nombre de pages illustrées ne peut pas dépasser le nombre total de pages estimé.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/edition/order", {
        mode, title: title.trim(),
        characters: Number(characters) || 0,
        pages: Number(pages) || 0,
        illustrated_pages: Number(illustratedPages) || 0,
        total_pages_estimate: Number(totalPagesEst) || 0,
        has_existing_layout: mode === "magazine" ? true : (mode === "magazine_new" ? false : hasLayout),
        format_final: format.trim(),
        destination, printer_name: printer.trim(), printer_specs_text: printerSpecs.trim(),
        notes: notes.trim(),
        file_ids: files.map((f) => f.id).filter(Boolean),
        specs_file_ids: specsFiles.map((f) => f.id).filter(Boolean),
        confirm: true,
      });
      toast.success("Votre demande est bien enregistrée.");
      nav(`/espace/commande/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const currentMode = MODES.find((m) => m.key === mode);

  return (
    <div className="cs-cream min-h-[85vh] py-14" data-testid="page-edition-configurator">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <Link to="/" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2"><ArrowLeft size={14} /> Retour à l'accueil</Link>
        <div className="mt-4">
          <div className="cs-eyebrow text-[#0E7C90]">Mise en page & édition · Configurateur</div>
          <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Mise en page & édition</h1>
          <p className="mt-3 text-neutral-700 max-w-2xl text-[15px] leading-relaxed">
            Cette rubrique concerne les documents composés de plusieurs pages : livres, livrets, brochures, magazines et revues.
          </p>
          <div className="mt-4 border border-amber-400 bg-amber-50/60 p-4 flex items-start gap-3">
            <div className="text-[13px] leading-relaxed">
              <strong>Vous souhaitez plutôt créer un support unique ?</strong> Pour une carte, un flyer, un faire-part, une invitation,
              une affiche, un marque-page, une réglette ou un dépliant, utilisez « Créer mon support ».{" "}
              <Link to="/creer-mon-support" className="cs-link-underline">Créer mon support →</Link>
            </div>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-8">
          <div className="space-y-6">
            <section className="cs-card p-6">
              <div className="cs-eyebrow text-neutral-500">Type de document</div>
              <div className="mt-3 grid gap-2">
                {MODES.map((m) => (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`mode-${m.key}`}
                    className={`text-left px-4 py-3 border ${mode === m.key ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>
                    <div className="font-serif text-[15px]">{m.label}</div>
                    <div className="text-[12px] text-neutral-500 mt-1">{m.desc}</div>
                    <div className="text-[12px] font-mono text-[#0E7C90] mt-1">{m.unit}</div>
                  </button>
                ))}
              </div>
            </section>

            {mode === "magazine" && (
              <section className="cs-card p-5">
                <div className="cs-eyebrow text-neutral-500">Disposez-vous déjà d'une maquette éditoriale ?</div>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => setHasLayout(true)} data-testid="layout-yes" className={`px-4 py-2 border ${hasLayout === true ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Oui</button>
                  <button type="button" onClick={() => setHasLayout(false)} data-testid="layout-no" className={`px-4 py-2 border ${hasLayout === false ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>Non</button>
                </div>
                {hasLayout === false && (
                  <div className="mt-3 border-l-2 border-[#0E7C90] pl-3 text-[13px]">
                    Votre publication a besoin d'une maquette éditoriale.
                    <button type="button" onClick={() => { setMode("magazine_new"); setHasLayout(null); }} className="mt-2 inline-flex items-center gap-1 cs-link-underline text-[#0E7C90]">Créer ma maquette →</button>
                  </div>
                )}
              </section>
            )}

            <section className="cs-card p-6 space-y-4">
              <div className="cs-eyebrow text-neutral-500">Informations du projet</div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500 uppercase tracking-[0.14em]">Titre du projet</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} className="cs-input mt-2 w-full" data-testid="edition-title" /></label>
              {isCharBased && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <label><span className="text-[11px] font-mono text-neutral-500">Caractères espaces compris</span>
                    <input type="number" min="0" value={characters} onChange={(e) => setCharacters(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-characters" />
                    <span className="block text-[11px] text-neutral-500 mt-1">Statistiques de votre logiciel de traitement de texte.</span></label>
                  {mode === "illustrated" && (<>
                    <label><span className="text-[11px] font-mono text-neutral-500">Pages illustrées</span>
                      <input type="number" min="0" value={illustratedPages} onChange={(e) => setIllustratedPages(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-illustrated" />
                      <span className="block text-[11px] text-neutral-500 mt-1">Seules les pages avec une illustration/photo sont facturées.</span></label>
                    <label className="sm:col-span-2"><span className="text-[11px] font-mono text-neutral-500">Pages totales estimées (indicatif)</span>
                      <input type="number" min="0" value={totalPagesEst} onChange={(e) => setTotalPagesEst(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-total-pages" /></label>
                    {illustratedInvalid && (
                      <div role="alert" aria-live="polite" className="sm:col-span-2 text-[13px] text-red-700 border border-red-400 bg-red-50 px-3 py-2" data-testid="illustrated-error">
                        Le nombre de pages illustrées ne peut pas dépasser le nombre total de pages estimé.
                      </div>
                    )}
                  </>)}
                </div>
              )}
              {isPageBased && !magazineMissing && (
                <label className="block"><span className="text-[11px] font-mono text-neutral-500">Nombre de pages à mettre en page</span>
                  <input type="number" min="1" value={pages} onChange={(e) => setPages(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-pages" /></label>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <label><span className="text-[11px] font-mono text-neutral-500">Format final</span>
                  <input value={format} onChange={(e) => setFormat(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-format" placeholder="Ex. A5, 148 × 210 mm…" /></label>
                <label><span className="text-[11px] font-mono text-neutral-500">Imprimeur (facultatif)</span>
                  <input value={printer} onChange={(e) => setPrinter(e.target.value)} className="cs-input mt-1 w-full" data-testid="edition-printer" /></label>
              </div>
              <div>
                <span className="text-[11px] font-mono text-neutral-500">Destination</span>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {DESTS.map((d) => (
                    <button key={d.k} type="button" onClick={() => setDestination(d.k)} data-testid={`edition-dest-${d.k}`}
                      className={`text-left px-3 py-2 border text-[13px] ${destination === d.k ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>{d.l}</button>
                  ))}
                </div>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500">Spécifications imprimeur (facultatif)</span>
                <textarea rows={3} value={printerSpecs} onChange={(e) => setPrinterSpecs(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="edition-specs" /></label>
              <div>
                <span className="text-[11px] font-mono text-neutral-500">Fichiers du projet (manuscrit, maquette, illustrations…)</span>
                <div className="mt-2"><FileUploader files={files} setFiles={setFiles} testId="edition-files" /></div>
              </div>
              <div>
                <span className="text-[11px] font-mono text-neutral-500">Consignes imprimeur — fichier(s)</span>
                <div className="mt-2"><FileUploader files={specsFiles} setFiles={setSpecsFiles} testId="edition-specs-files" category="printer_specs" /></div>
              </div>
              <label className="block"><span className="text-[11px] font-mono text-neutral-500">Une précision ?</span>
                <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="cs-input mt-1 w-full resize-none" data-testid="edition-notes" /></label>
            </section>

            <section className="cs-card p-6 space-y-3">
              <div className="cs-eyebrow text-neutral-500">Récapitulatif</div>
              <div className="text-sm"><strong>{currentMode?.label}</strong>{title && <> — <em>{title}</em></>}</div>
              {quote.rows.map((r, i) => (
                <div key={i} className="flex justify-between text-[13px]"><span>{r.label}</span><span className="font-mono">{fmtEUR(r.amt)}</span></div>
              ))}
              <div className="flex justify-between font-serif text-xl border-t border-black/10 pt-2"><span>Total estimé</span><span>{fmtEUR(quote.total)}</span></div>
              <ul className="mt-3 text-[13px] space-y-1">
                {["Mise en page complète", "Préparation prépresse", "Fonds perdus lorsque nécessaires", "Fichier prêt imprimeur", "2 séries de modifications mineures regroupées"].map((x, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={14} className="text-[#0E7C90] mt-0.5" />{x}</li>
                ))}
              </ul>
              <p className="text-[12px] font-mono text-neutral-500">Aucun supplément sans votre accord.</p>
            </section>

            <section className="cs-card p-6 space-y-3">
              <div className="cs-eyebrow text-neutral-500">Confirmation</div>
              <label className="flex items-start gap-3 text-[13px]">
                <input type="checkbox" checked={confirmChk} onChange={(e) => setConfirmChk(e.target.checked)} data-testid="edition-confirm" className="mt-1" />
                <span>Je confirme que les informations, le volume de texte et le nombre de pages illustrées indiqués correspondent à mon projet.</span>
              </label>
              <button onClick={submit} disabled={!confirmChk || submitting || magazineMissing || illustratedInvalid} data-testid="edition-submit"
                className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? "Envoi…" : `Confirmer ma demande — ${fmtEUR(quote.total)}`}
              </button>
            </section>
          </div>

          <aside className="lg:sticky lg:top-4 h-fit">
            <div className="cs-card p-5">
              <div className="cs-eyebrow text-neutral-500">Prix estimé</div>
              <div className="mt-3 font-serif text-4xl">{fmtEUR(quote.total)}</div>
              <div className="mt-3 text-[12px] text-neutral-600 leading-relaxed">
                {currentMode?.unit}
              </div>
              <div className="mt-4 border-t border-black/10 pt-3 text-[12px] text-neutral-500">
                Besoin également d'une couverture ? <Link to="/services/couvertures" className="cs-link-underline">Voir les couvertures →</Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
